/**
 * Form for editing a single character's script data within a SIM level.
 *
 * @module components/sim/createLevels/CharacterData
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Divider,
    Grid,
    Toolbar,
    Box,
    Typography,
    TextField,
    Radio,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Tooltip,
    TextareaAutosize,
    InputLabel,
} from '@material-ui/core';
import TagsInput from 'react-tagsinput-special';
import 'react-tagsinput-special/react-tagsinput.css';
import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import LinearProgress from '@material-ui/core/LinearProgress';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import IconButton from "@material-ui/core/IconButton";
import { useDrag } from 'react-dnd';
import {
    generateAudio,
    uploadRecordedAudio,
} from '../../../services/result.service';
import {
    uploadAudio,
    getAwsPresignedUrl,
} from "../../../services/result.service";
import MicrophoneStream from "microphone-stream";
import {
    convertAudioToBinaryMessage,
    handleEventStreamMessage,
    resetEventStreamMessage,
    getAudioEventMessage,
} from "../../../utils/convertIntoBuffer";
import { useCharacterDataStyle } from './CharacterData.js';
import Autopopulate from './Autopopulate.jsx';
import GenerateAudio from './GenerateAudio.jsx';
import { EventStreamCodec } from "@aws-sdk/eventstream-codec";
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import {isUrlExpired} from "../../../utils/url";
import CharacterAvatar from "../../common/CharacterAvatar";
import AudioPlayButton from "../../common/AudioPlayButton";


/**
 * CharacterData component designed for rendering and managing state related to
 * character data for a simulation-based application. It includes various handlers
 * to manage user interactions, state updates, and other operations relevant to
 * characters.
 *
 * @param {Object} props - The properties passed to the component, specifically related to character configuration and state management.
 * @param {number} props.index - The index of the character in the list.
 * @param {Object} props.data - The data object containing details of the character.
 * @param {Array<Object>} props.characterData - Array of character data objects.
 * @param {Function} props.handleChange - Handler for general state change, usually for text fields.
 * @param {Function} props.handleAudioChange - Handler for managing audio-related state changes.
 * @param {Function} props.handleKeywordChange - Handler for keyword-related state changes.
 * @param {Function} props.handleTestChange - Handler for updating test-related information.
 * @param {Function} props.handleCheckboxChange - Handler for managing checkbox toggle state.
 * @param {Function} props.handleAudioGenerate - Handler for generating or processing audio.
 * @param {Function} props.handleRadioChange - Handler for updating state related to radio buttons.
 * @param {Function} props.handleDeleteScript - Handler for deleting scripts associated with the character.
 * @param {Function} props.handleAgentRecordedAudio - Handler for managing agent-recorded audio data.
 * @param {Function} props.handleAutopopulateChange - Handler for toggling autopopulate-related settings.
 * @param {Function} props.handleResetAutopopulate - Handler to reset all autopopulated fields.
 * @param {Function} props.updateData - Updates character data with new or modified values.
 * @param {string} props.borderColor - Specifies the border color for character UI elements.
 * @param {Object} props.sim - The simulation object tied to the current character.
 * @param {Array<Object>} props.tabs - Array of tab data associated with auto-population.
 * @param {number} props.levelOrder - Specifies the order or level of the character.
 * @param {boolean} props.newSim - Indicates whether the current simulation is new.
 * @param {Array<string>} props.currentVoice - Array of voice options for the character.
 * @param {string} props.path - The path or unique identifier of the character's data.
 * @param {boolean} props.onlyKeywords - Determines whether only keywords are being managed.
 *
 * @returns {JSX.Element} - Returns the rendered component for the CharacterData module.
 */
const CharacterData = ({
    index,
    data,
    characterData,
    handleChange,
    handleAudioChange,
    handleKeywordChange,
    handleTestChange,
    handleCheckboxChange,
    handleAudioGenerate,
    handleRadioChange,
    handleDeleteScript,
    handleAgentRecordedAudio,
    handleAutopopulateChange,
    handleResetAutopopulate,
    updateData,
    borderColor,
    sim,
    tabs,
    levelOrder,
    newSim,
    currentVoice,
    path,
    onlyKeywords,
}) => {
    const selectedCharacter = characterData.find(
        (item) => item._id === data.sim_character_id,
    );
    const classes = useCharacterDataStyle();
    const [addTextArea, setTextArea] = useState(false);
    const [playAudio, setPlayAudio] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [testResults,           setTestResults] = useState(false);
    const [isLoadingTestResults,  setIsLoadingTestResults] = useState(false);
    const [gradeScore,            setGradeScore] = useState(0);
    const [matchTests,            setmatchTests] = useState([]);
    const [missesTests,           setmissesTests] = useState([]);
    const [finalTranscription,    setFinalTranscription] = useState('');
    const [presignedUrl, setPresignedUrl] = useState("");
    const [presignedBool, setPresignedBool] = useState(false);
    const [open, setOpen] = React.useState(false);
    const [openKeyword, setOpenKeyword] = React.useState(false);
    const [openTest, setOpenTest] = React.useState(false);
    const [recordingTest, setRecordingTest] = React.useState(false);
    const [tryTestOpen, setTryTestOpen] = React.useState(false);
    const [openTestEdit, setOpenTestEdit] = React.useState(false);
    const [hintText, setHintText] = React.useState(data.hint_text);
    const [keywords, setKeywords] = React.useState(data.keywords);
    const [hideConfidence, setHideConfidence] = React.useState(true);
    const [scriptOveralConfidence, setScriptOveralConfidence] = React.useState(data.overalConfidence || 60 );
    const [tests, setTests] = React.useState( data.tests || []);
    const [testId, setTestId] = React.useState(0);
    const [timer, setTimer] = useState(0);
    //const [record, setRecord] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [test, setTest] = React.useState( {
        name:'',
        isFlexible: false,
        keywords: [String],
        coachFeedback: '',
        confidence: 0
    } );
    const [testNameError, setTestNameError] = useState('');
    const [testWordsError, setTestWordsError] = useState('');
    const [name, setName] = useState('');
    const [isFlexible, setIsFlexible] = useState(false);
    const [coachFeedback, setCoachFeedback] = useState('');
    const [testKeywords, setTestKeywords] = useState([]);
    const [confidence, setConfidence] = useState(0);
    const [tags, setTags] = useState([]); //eslint-disable-line no-unused-vars
    const [editMode, setEditMode] = useState(false);
    const [errorTag, setErrorTag] = useState(false);
    const [editedTags, setEditedTags] = useState(''); // Display tags as comma-separated string in the input field
    const [script, setScript] = React.useState(data.script);
    const [switchGenerate, setSwitchGenerate] = useState(false);
    const [voice, setVoice] = useState(currentVoice[0]);
    const [isRecording, setIsRecording] = useState(false);
    const [openDialogBox, setOpenDialogBox] = useState(false);
    const [openScriptDialogBox, setOpenScriptDialogBox] = useState(false);
    const [
        openExistingScriptDialogBox,
        setOpenExistingScriptDialogBox,
    ] = useState(false);
    const rec = useRef(null);
    const stream = useRef(null);
    let blob;
    let blobs;
    let voiceStream;
    const [caseInformationArray, setCaseInformationArray] = useState([]);
    const [patientInformationArray, setPatientInformationArray] = useState([]);
    const [acmeInformationArray, setAcmeInformationArray] = useState([]);
    const [loremIpsumInformationArray, setLoremIpsumInformationArray] = useState(
        [],
    );
    const [openAlert, setOpenAlert] = useState(false);
    const [genAudio, setGenAudio] = useState(false);
    const [bgColor, setbgColor] = useState(true);
    const node = useRef();
    const ref = useRef(null);
    const [{isDragging}, drag] = useDrag(() => ({
        type: 'component',
        item: { id: data._id, path },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }));
    const opacity = isDragging ? 0 : 1;
    const util_utf8_node = require("@aws-sdk/util-utf8-node"); // utilities for encoding and decoding UTF8

    //const eventStreamMarshaller = new marshaller.EventStreamMarshaller( // EventStreamMarshaller is deprecated
    const eventStreamMarshaller = new EventStreamCodec(
        util_utf8_node.toUtf8,
        util_utf8_node.fromUtf8
    );

    useEffect(() => {
        if (openAlert) {
            setTimeout(() => {
                setOpenAlert(false);
            }, 3000);
        }
    }, [openAlert]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    useEffect(() => {
        getAwsPresignedUrl()
            .then((res) => {
                return setPresignedUrl(res.data.url);
            })
            .catch((err) => console.error(err));
        setTimer(0);
    }, [setPresignedUrl, presignedBool]);

    useEffect(() => {
        setHintText(data.hint_text);
    }, [data.hint_text]);

    useEffect(
        () => {
            const array1 = [];
            const array2 = [];
            const array3 = [];
            const array4 = [];
            if (data.autopopulate_data && data.autopopulate_data.length && data.autopopulate_data[0].tabs && data.autopopulate_data[0].tabs.length > 0) {
                for (const keyword of data.autopopulate_data[0].tabs[0].sections) {
                    for (const field of keyword.fields) {
                        if (keyword.section_name === 'Case Information( Header visible on edit only)') {
                            if (field.is_enabled) {
                                array1.push(field.field_title);
                            }
                        } else if (keyword.section_name === 'Additional Information') {
                            if (field.is_enabled) {
                                array2.push(field.field_title);
                            }
                        } else if (keyword.section_name === 'Description Information(Header Visible on edit only)') {
                            if (field.is_enabled) {
                                array3.push(field.field_title);
                            }
                        } else if (keyword.section_name === 'Web Information(Header Not visible on edit only)') {
                            if (field.is_enabled) {
                                array4.push(field.field_title);
                            }
                        }
                    }
                }
            }
            setCaseInformationArray([...array1]);
            setPatientInformationArray([...array2]);
            setAcmeInformationArray([...array3]);
            setLoremIpsumInformationArray([...array4]);
        },
        [data.autopopulate_data, data._id]
    );

    useEffect(() => {
        if (data.newScript) {
            setTimeout(() => {
                setbgColor(false);
            }, 3000);
        }
    }, [data]);

    /**
     * Updates the state variable for name based on user input.
     *
     * This function is triggered by an event, typically from a text input field.
     * It extracts the value from the event's target and updates the state
     * to reflect the current input.
     *
     * @param {Object} event - The event object generated by the input field.
     * @param {Object} event.target - The target element where the event occurred.
     * @param {string} event.target.value - The current value of the input field.
     */
    const handleNameChange = (event) => {
        setName(event.target.value);
    };

    /**
     * Handles the change event triggered by an input element to update the flexible state.
     *
     * Depending on the value of the input element, this function adjusts the state for
     * whether the item is flexible and controls the visibility of a related confidence field.
     *
     * @param {Object} event - The event object triggered by the input change.
     * @param {Object} event.target - The target element that triggered the event.
     * @param {string} event.target.value - The value of the target input element.
     */
    const handleIsFlexibleChange = (event) => {
        if (event.target.value === 'flexible') {
            setIsFlexible(true);
            setHideConfidence(false);
        } else {
            setIsFlexible(false);
            setHideConfidence(true);
        }
        //setIsFlexible(event.target.checked);
    };

    /**
     * Updates the state of coach feedback based on user input.
     *
     * This function is triggered when there is a change in the input field associated
     * with coach feedback. The event parameter provides access to the new value entered
     * in the input field, which is then used to update the `coachFeedback` state.
     *
     * @param {Object} event - The event object associated with the change event.
     * @param {Object} event.target - The target element that triggered the event.
     * @param {string} event.target.value - The new value of the input field.
     */
    const handleCoachFeedbackChange = (event) => {
        setCoachFeedback(event.target.value);
    };

    /**
     * Handles the change in keywords/tags input by the user.
     *
     * Updates the state with the new array of tags provided.
     *
     * @param {Array<string>} tags - An array of strings representing the new set of keywords or tags.
     */
    const handleKeywordsChange = (tags) => {
        setTestKeywords(tags); // Set the extracted tag array as the new value for testKeywords
    };

    /**
     * Handles changes to the confidence input field.
     *
     * Parses the input value from the event, converts it to an integer,
     * and updates the confidence state. If the parsed value is not a number,
     * it defaults to 0.
     *
     * @param {Object} event - The event object representing the change event.
     * @param {Object} event.target - The target element where the value is input.
     * @param {string} event.target.value - The input value to be parsed and used.
     */
    const handleConfidenceChange = (event) => {
        const value = parseInt(event.target.value, 10);
        setConfidence(isNaN(value) ? 0 : value);
    };

    /**
     * Handles the logic for initiating the edit action on tags.
     *
     * This function updates the state to enable edit mode and sets the
     * current tags in an editable format by joining them into a single
     * comma-separated string.
     *
     * Side Effects:
     * - Updates the `editedTags` state with a comma-separated string of keywords.
     * - Activates the edit mode by setting the `editMode` state to true.
     */
    const handleEditClick = () => {
        setEditedTags(testKeywords.join(', '));
        setEditMode(true);
    };

    /**
     * Handles the logic for saving and validating edited tags.
     *
     * This function processes the `editedTags` input to ensure that the tags meet the required format and constraints
     * before updating the state. Tags are validated to ensure they are unique, allow a limited number of logical operators
     * (`&` and `|`), and comply with specific constraints regarding their usage.
     *
     * Validation Rules:
     * - Tags are separated by commas and trimmed of leading/trailing whitespace.
     * - Logical operators (`&`, `|`) are allowed but must comply with the following:
     *   - No more than one `&` or `|` operator is allowed.
     *   - Only one logical operator (`&` or `|`) can exist within the tag list.
     * - Tags must be unique.
     *
     * Side Effects:
     * - Sets an error message if the input is invalid or empty.
     * - Updates the `tags` and `testKeywords` states with valid, processed tags.
     * - Exits edit mode on successful validation.
     * - Displays an error message for 3 seconds if validation fails.
     *
     * Dependencies:
     * - `editedTags`: Input string containing comma-separated tags to be processed.
     * - `setTags`: Updates the final state of valid tags.
     * - `setTestKeywords`: Updates the state of validated test keywords.
     * - `setTestWordsError`: Displays an error message if no keywords are provided.
     * - `setEditMode`: Toggles the edit mode state.
     * - `setErrorTag`: Temporarily displays an error state when validation fails.
     */
    const handleSaveClick = () => {
        // Split the editedTags string by commas to get the updated tags
        if (!editedTags.length) {
            setTestWordsError('Please enter at least one keyword');
            return;
        }
        const updatedTags = editedTags.split(',').map((tag) => tag.trim());
        const uniqueTags = Array.from(new Set(updatedTags));
        const andOperatorCount = updatedTags.filter((tag) => tag === '&').length;
        const orOperatorCount = updatedTags.filter((tag) => tag === '|').length;

        if (
            (
                uniqueTags.length === updatedTags.length &&
                (andOperatorCount === 1 || orOperatorCount === 1) &&
                andOperatorCount + orOperatorCount <= 1
            ) ||
            (andOperatorCount + orOperatorCount === 0)
        ) {
            setTags(updatedTags);
            setTestKeywords(updatedTags)
            setEditMode(false);
        } else {
            setErrorTag(true);
            setTimeout(() => {
                setErrorTag(false); // Clear the error message after 3 seconds
            }, 3000);
        }
    };

    // To hide the voice dropdown if clicked outside it's div/section.
    /**
     * Event handler function for managing click events.
     * Determines whether the click event occurred inside or outside a specified DOM node
     * and updates the state accordingly.
     *
     * @param {Event} e - The click event object.
     */
    const handleClick = (e) => {
        if (node && node.current && node.current.contains(e.target)) {
            setGenAudio(true);
            // inside click
            return;
        }
        // outside click
        setGenAudio(false);
    };

    /**
     * Toggles the visibility of a text area and optionally updates the script based on certain conditions.
     *
     * @param {boolean} flag - Determines whether the text area should be shown or hidden.
     */
    const showTextArea = (flag) => {
        setTextArea(flag);
        if (onlyKeywords && selectedCharacter.type === 'agent') {
            setScript(`Use Keyword(s): ${keywords.join(',')}`);
        }
    };

    /**
     * A callback function that sets the state to open.
     *
     * This function is typically used to handle events, such as button clicks,
     * and updates the component state to indicate an "open" state.
     */
    const handleClickOpen = () => {
        setOpen(true);
    };

    /**
     * Closes the current component or dialog by setting its open state to false.
     *
     * This function is typically used as an event handler to update the state and
     * trigger the closing of a modal, dialog, or any component controlled by the `open` state.
     */
    const handleClose = () => {
        setOpen(false);
    };

    /**
     * A function to handle the opening of the Keywords modal
     *
     * This function sets the keyword data, typically from an external source or predefined dataset,
     * and updates the state to indicate that the keyword dialog, menu, or component is open.
     *
     * @function handleKeywordOpen
     * @returns {void}
     */
    const handleKeywordOpen = () => {
        setKeywords(data.keywords);
        setOpenKeyword(true);
    };

    /**
     * A function to handle the cancellation of keyword selection or editing.
     * It resets the keyword state to its original value and closes the keyword modal or selection UI.
     *
     * @function handleKeywordCancel
     * @returns {void} This function does not return a value.
     */
    const handleKeywordCancel = () => {
        setKeywords(data.keywords);
        setOpenKeyword(false);
    };

    /**
     * Asynchronous function to handle the process of opening the "Try Test" mode.
     * If the test editing mode is active, it attempts to save the test before proceeding.
     * If the save operation is successful or unnecessary, the "Try Test" mode is enabled.
     *
     * @async
     * @function
     */
    const handleTryTestOpen= async () => {
        let saved = true;
        if (openTestEdit) {
            saved = await handleTestSave();
        }
        //setTests(data.tests);
        if(saved) {
            setTryTestOpen(true);
        }
    };

    /**
     * Handles the logic for closing the "Try Test" feature.
     *
     * This function performs the following actions:
     * - Resets the "Try Test" modal or feature state by closing it.
     * - Clears the final transcription.
     * - Resets the test results to a default or false state.
     */
    const handleTryTestClose= () => {
        //setTests(data.tests);
        setTryTestOpen(false);
        setFinalTranscription('');
        setTestResults(false);
    };

    /**
     * Function to handle the opening of a test.
     *
     * This function updates the application state to reflect that a test is being opened.
     * It prepares any required data or state changes to facilitate the opening process.
     *
     * The function performs the following actions:
     * - Executes logic to manage or process test-related operations.
     * - Sets the application state to indicate that the test interface or section is now active.
     */
    const handleTestOpen = () => {
        //setTests(data.tests);
        setOpenTest(true);
    };

    /**
     * Resets test-related state values and closes the test editing interface.
     *
     * This function is typically used to handle the cancellation of test modifications or editing.
     * It performs the following operations:
     * - Clears the test name error state.
     * - Clears the test words error state.
     * - Sets the test editing UI to a closed state.
     */
    const handleTestCancel = () => {
        //setTests(data.tests);
        setTestNameError('');
        setTestWordsError('');
        setOpenTestEdit(false);
    };

    /**
     * Handles the closure of the test-related modal dialog.
     *
     * This function is responsible for performing cleanup operations when the
     * test modal is closed. It resets error states for test name and test words,
     * and ensures that both the test creation and test editing modals are closed.
     *
     * Effects:
     * - Clears the error message for the test name by setting it to an empty string.
     * - Clears the error message for the test words by setting it to an empty string.
     * - Closes the test creation modal by setting its state to false.
     * - Closes the test editing modal by setting its state to false.
     */
    const handleTestCloseModal = () => {
        setTestNameError('');
        setTestWordsError('');
        setOpenTest(false);
        setOpenTestEdit(false);
    };

    /**
     * Handles the closure of the test-related dialogs or views.
     *
     * This function performs the following actions:
     * - Closes the test dialog by setting the `openTest` state to `false`.
     * - Closes the test edit dialog by setting the `openTestEdit` state to `false`.
     * - Triggers the `save` function to persist any relevant changes.
     */
    const handleTestClose = () => {
        setOpenTest(false);
        setOpenTestEdit(false);
        save();
    };

    /**
     * Handles the editing of a test by setting the relevant state variables based on the provided index.
     *
     * This function updates the test-related state information such as the test's ID, name, flexibility,
     * confidence level, keywords, and coaching feedback. If the provided index is valid (greater than -1),
     * the state is populated with the corresponding test's data. If the index is invalid (-1), the state
     * is reset to default values. Additionally, this function controls the visibility of the confidence-related
     * UI based on the flexibility of the selected test.
     *
     * @param {number} idx - The index of the test to edit. A value greater than -1 indicates a valid test,
     * while -1 is used to reset the state to default.
     */
    const handleTestEdit = (idx) => {
        setTestId(idx);
        if (idx > -1) {
            setTest(data.tests[idx]);
            setName(data.tests[idx].name);
            setIsFlexible(data.tests[idx].isFlexible);
            if (data.tests[idx].isFlexible) {
                setHideConfidence(false);
            } else {
                setHideConfidence(true);
            }
            setCoachFeedback(data.tests[idx].coachFeedback);
            setTestKeywords([...data.tests[idx].keywords]);
            setConfidence(data.tests[idx].confidence);
        } else{
            setName('');
            setIsFlexible(false);
            setCoachFeedback('');
            setTestKeywords([]);
            setConfidence(0);
            setHideConfidence(true);
        }
        setOpenTestEdit(true);
    };

    /**
     * Handles the saving of a test configuration, updating the test list and associated data.
     *
     * This function validates the test details before saving. If validation is successful, it
     * updates the test in the existing test list or adds it as a new entry if no matching test
     * is found. It also updates various state variables and sends the updated data to the back end.
     *
     * Validation Rules:
     * - A name must be specified for the test.
     * - At least one keyword must be provided for the test.
     *
     * Actions Performed on Successful Save:
     * - Updates the state variable storing the test (`setTest`).
     * - Modifies the global test array (`setTests`) with the new test data.
     * - Notifies the parent component of the test changes (`handleTestChange`).
     * - Updates the corresponding test data in the provided data structure (`data.tests`).
     * - Closes any modal or edit dialogs.
     * - Resets all input fields and state variables related to the test configuration.
     * - Sends the updated data to the server via an asynchronous call (`updateData`).
     *
     * Return:
     * - Returns a promise that resolves to `true` upon successful completion.
     * - Returns `false` immediately if validation fails.
     */
    const handleTestSave = async () => {
        let auxTest = {
            name: name,
            isFlexible: isFlexible,
            keywords: [...testKeywords],
            coachFeedback: coachFeedback,
            confidence: confidence,
        };

        if (!name.length) {
            setTestNameError('Please enter a name for the test');
            return false;
        }

        if (!testKeywords.length) {
            setTestWordsError('Please enter at least one keyword');
            setErrorTag(true);
            return false;
        }

        setTest(auxTest);
        let aux = [...tests]; // Create a new array using spread operator
        if (testId > -1) {
            aux[testId] = auxTest;
        } else {
            aux.push(auxTest);
        }
        setTests(aux);
        handleTestChange(index, aux);
        data.tests = aux;
        setOpenTest(false);
        setOpenTestEdit(false);
        setTestId(-1);
        await updateData(index, data);
        setName('');
        setIsFlexible(false);
        setCoachFeedback('');
        setTestKeywords([]);
        setConfidence(0);
        return true;
    };

    /**
     * Asynchronously handles the removal of a test from the list of tests and updates the corresponding state and data.
     *
     * This function performs the following steps:
     * - Filters out the test identified by `testId` from the `tests` array.
     * - Updates the state with the modified array of tests.
     * - Triggers the parent component's `handleTestChange` method to synchronize the updated data.
     * - Updates the `data.tests` property with the modified list of tests.
     * - Closes the test-related modals by setting the respective state variables to `false`.
     * - Resets the `testId` to its default value of `-1`.
     * - Calls the `updateData` method to persist the changes.
     * - Resets various UI-related state values such as test name, flexibility flag, coach feedback, test keywords, and confidence score.
     */
    const handleRemoveTest = async () => {
        let aux = tests.filter((test, idx) => idx !== testId);
        setTests(aux);
        handleTestChange(index, aux);
        data.tests = aux;
        setOpenTest(false);
        setOpenTestEdit(false);
        setTestId(-1);
        await updateData(index, data);
        setName('');
        setIsFlexible(false);
        setCoachFeedback('');
        setTestKeywords([]);
        setConfidence(0);
    };

    /**
     * Handles changes to text input fields and updates the corresponding state variables based on the input's name attribute.
     *
     * @param {number} index - The index of the input field being changed, typically used when dealing with arrays or lists of inputs.
     * @param {Event} e - The event object generated by the input field, containing information about the input interaction, such as the target element and its value.
     *
     * The function checks the `name` property of the input field (`e.target.name`) to determine what state variable to update:
     * - If `e.target.name` is 'hint_text', it updates the hint text state.
     * - If `e.target.name` is 'script', it updates the script state.
     */
    const handleTextChange = (index, e) => {
        if (e.target.name === 'hint_text') {
            setHintText(e.target.value);
        }
        if (e.target.name === 'script') {
            setScript(e.target.value);
        }
    };

    /**
     * Updates the state variable `keywords` with the given value.
     *
     * @function keywordsChange
     * @param {Event} e - The event object containing the new value for the `keywords`.
     */
    const keywordsChange = (e) => {
        setKeywords(e);
    };

    /**
     * Asynchronous function to handle the saving of keyword-related data.
     *
     * This function performs the following:
     * 1. Updates the keyword state by invoking `handleKeywordChange`.
     * 2. Closes the keyword input dialog by setting `setOpenKeyword` to false.
     * 3. If `onlyKeywords` is true, updates the `data.script` with the provided keywords.
     * 4. Invokes `updateData` to persist the updated data at the specified index.
     *
     * @async
     * @function handleKeywordSave
     * @returns {Promise<void>} A promise that resolves when all operations are complete.
     */
    const handleKeywordSave = async () => {
        handleKeywordChange(index, keywords);
        setOpenKeyword(false);
        if (onlyKeywords) {
            data.script = `Use Keyword(s): ${keywords.join(',')}`;
        }
        await updateData(index, data);
    };

    /**
     * An asynchronous function that processes and saves hint text data.
     *
     * This function performs three main tasks:
     * 1. Calls a handler function to update the hint text value.
     * 2. Closes an associated dialog or form by updating the state.
     * 3. Executes an asynchronous operation to update external or persisted data.
     *
     * Dependencies:
     * - `handleChange`: A function invoked to manage and update hint text values based on specified criteria.
     * - `setOpen`: A state management function to control the visibility of a UI component.
     * - `updateData`: An asynchronous function responsible for persisting or synchronizing data changes.
     *
     * Variables:
     * - `hintText`: The value of the hint text to be processed and saved.
     * - `index`: The position or identifier of the specific data item being updated.
     * - `data`: The dataset or object containing the current state of information.
     */
    const hintTextSave = async () => {
        handleChange(hintText, index, 'hint_text');
        setOpen(false);
        await updateData(index, data);
    };

    /**
     * Handles the change in overall confidence level for a script.
     *
     * This function is triggered by an event, typically from a user input element,
     * and performs the following actions:
     * - Updates the script's overall confidence value using the `setScriptOveralConfidence` function.
     * - Calls a handler to propagate the change, passing the new value, the current index,
     *   and the key `'overalConfidence'`.
     * - Optionally interacts with back-end or local data persistence by awaiting
     *   the completion of the `updateData` function with relevant arguments.
     *
     * @param {Object} event - The event object triggered by user interaction.
     * @param {Object} event.target - The target element of the event.
     * @param {string|number} event.target.value - The new value for the script's overall confidence.
     */
    const handleScriptOveralConfidenceChange = async (event) => {
        setScriptOveralConfidence(event.target.value);
        handleChange(event.target.value, index, 'overalConfidence');
        /* setOpen(false);*/
        await updateData(index, data);
    };

    /**
     * Asynchronously handles the process of saving a script.
     *
     * This function performs the following actions:
     * 1. Triggers a change event for the specified script at the given index.
     * 2. Checks if the `audio_url` exists in the `data` object:
     *    - If `audio_url` exists, it opens an existing script dialog box.
     *    - If `audio_url` does not exist, it opens a new script dialog box.
     * 3. Updates the data for the specified index.
     *
     * @async
     * @function saveScript
     * @returns {Promise<void>} Resolves when all processing is completed.
     */
    const saveScript = async () => {
        handleChange(script, index, 'script');
        if (data.audio_url) {
            setOpenExistingScriptDialogBox(true);
        } else {
            setOpenScriptDialogBox(true);
        }
        await updateData(index, data);
    };

    /**
     * Resets the script data and hides the text area.
     *
     * The `cancelScript` function reverts any changes made to the script by resetting
     * it to its initial state using the `setScript` method, and hides the text area
     * by invoking the `showTextArea` method with `false` as an argument.
     *
     * @function cancelScript
     * @returns {void} Does not return any value.
     */
    const cancelScript = () => {
        setScript(data.script);
        showTextArea(false);
    };

    /**
     * Resets the hint text to its default value and closes the current UI element or dialog.
     *
     * This function is typically used to cancel or revert changes made to the hint text.
     * It sets the hint text state to the provided default value and ensures the UI element
     * or dialog associated with the hint text is closed.
     *
     * @function
     */
    const hintTextCancel = () => {
        setHintText(data.hint_text);
        setOpen(false);
    };

    /**
     * Saves the current state by closing the keyword input and updating the relevant data.
     *
     * This function performs the following actions:
     * 1. Closes the keyword input by setting `setOpenKeyword` to false.
     * 2. Updates the specific data entry at the given index using the provided `updateData` function.
     *
     * This is typically used to finalize changes made to a specific data item and ensure
     * the UI reflects the updated state.
     */
    const save = () => {
        setOpenKeyword(false);
        updateData(index, data);
    };

    /**
     * Handles the closure of the keyword input or dialog.
     *
     * This function performs two main actions:
     * 1. Sets the state to close the keyword-related component (e.g., a modal or dropdown).
     * 2. Triggers the save operation, which may involve persisting the current state or executing
     *    actions dependent on the user's input.
     */
    const handleKeywordClose = () => {
        setOpenKeyword(false);
        save();
    };

    /**
     * Asynchronously stops the currently playing audio by emulating a click event
     * on an AudioPlayButton component. This dispatches a custom event named
     * 'audio-button-click' to notify the system to halt audio playback.
     *
     * Note: This approach simulates user interaction via custom events. A more
     * robust solution would involve communicating directly with the AudioPlaylist
     * component to stop playback programmatically.
     *
     * Custom Event Details:
     * - Event Name: 'audio-button-click'
     * - Payload:
     *   - `id`: Identifier for the audio data.
     *   - `status`: Current playback status ('playing').
     */
    const stopCurrentPlayingAudio = async () => {
        // FIXME - I don't really like this, as it emulates the click event of
        //  and AudioPlayButton component. It would be better to have a way to
        //  ask the AudioPlaylist component to stop playing the current audio
        let evt = new CustomEvent(
            'audio-button-click',
            {
                detail: {
                    id: data._id,
                    status: 'playing'
                }
            }
        );
        document.dispatchEvent(evt);
    }

    /**
     * Asynchronously dispatches a custom event to update the current audio details.
     *
     * This function creates and dispatches a `CustomEvent` named 'audio-update'
     * with specific details about the audio, including its unique ID and URL.
     * The event is then dispatched to the `document` object.
     *
     * Event Detail Properties:
     * - `id` (string): The unique identifier of the audio resource.
     * - `url` (string|null): The URL of the audio resource. If neither
     *   `data.audio` nor `data.audio_url` is defined, this property will be `null`.
     */
    const updateCurrentAudio = async () => {
        let ev = new CustomEvent(
            'audio-update',
            {
                detail: {
                    id: data._id,
                    url: data.audio ?? data.audio_url ?? null
                }
            }
        );
        document.dispatchEvent(ev);
    }

    /**
     * Asynchronously handles the generation and playback of audio based on the current scenario and configurations.
     *
     * This function performs several tasks including stopping any currently playing audio, preparing request data
     * for audio generation, handling audio generation based on the given conditions, and finally, updating
     * relevant states such as loading status, voice settings, and playback controls.
     *
     * The logic accommodates different scenarios depending on whether the character is an agent,
     * the level order, and whether an agent-recorded audio is available.
     *
     * Steps:
     * 1. Stops any currently playing audio.
     * 2. Prepares the request for generating audio.
     * 3. Dynamically manages conditions for audio generation and processing.
     * 4. Handles updates to UI states and audio metadata.
     * 5. Triggers playback for the newly generated or existing audio.
     *
     * Key Conditions:
     * - If the character is an agent and at level 0 or 1, handle special audio conditions.
     * - Otherwise, use the provided agent-recorded audio or generate new audio.
     *
     * State Updates:
     * - Updates the `isLoading`, `genAudio`, `voice`, and `playAudio` states to reflect the process.
     * - Passes updated audio-related data to ensure proper handling in UI.
     *
     * Dependencies:
     * - `stopCurrentPlayingAudio`: Stops any currently playing audio.
     * - `generateAudio`: Generates new audio based on the provided request data.
     * - `handleAudioGenerate`: Processes the generated or existing audio and handles storage and metadata.
     * - `updateData`: Updates relevant data after audio processing.
     * - `updateCurrentAudio`: Finalizes the updating of the currently playing audio settings.
     */
    const showPlayAudio = async () => {
        const req = {};
        let response = {};

        setIsLoading(true);

       await stopCurrentPlayingAudio();

        if (genAudio) {
            req.voice = voice;
        }
        req.sim_script_id = data._id;

        if (data.agent_recorded_audio || selectedCharacter.type !== 'agent' || levelOrder === 0 || levelOrder === 1) {
            response = await generateAudio(req);
        }
        if (selectedCharacter.type === 'agent' && (levelOrder === 0 || levelOrder === 1)) {
            await handleAudioGenerate(
                index,
                response.audio_url,
                response.expiry_date,
                response.audio_key,
                true,
                voice,
            );
        } else {
            await handleAudioGenerate(
                index,
                response.audio_url,
                response.expiry_date,
                response.audio_key,
                data.agent_recorded_audio,
                voice,
            );
        }

        setGenAudio(false);
        await updateData(index, data);
        setVoice(voice);
        setIsLoading(false);
        setPlayAudio(true);

        await updateCurrentAudio();
    };

    /**
     * Handles the rendering of the play button for audio playback.
     *
     * This function conditionally renders an audio playback button based on the provided parameters.
     * It uses the `AudioPlayButton` component to represent the play button, which accepts the audio ID,
     * its availability status, and associated CSS classes. The availability of the button is determined
     * by the absence of the `audio` or `audio_url` within the `data` object.
     *
     * @param {boolean} audioPlay - Indicates whether the audio playback is currently active.
     * @param {boolean} audio - Specifies if an audio resource is available.
     * @param {Object} data - An object containing the metadata and state for the audio playback.
     * @param {string} [data._id] - Unique identifier for the audio resource.
     * @param {boolean} [data.play] - Indicates if the audio playback is active or paused.
     * @param {string} [data.audio] - The audio resource or file.
     * @param {string} [data.audio_url] - URL to the audio resource.
     * @returns {JSX.Element} A JSX element representing the play button or placeholder based on the audio state.
     */
    const showPlayButton = (audioPlay, audio, data) => {
        return (
            <AudioPlayButton audio_id={data?._id}
                             classes={classes}
            />
        );
    };

    /**
     * Asynchronous function that manages the process of starting a test audio recording, streaming the audio to a WebSocket service for transcription,
     * and handling the response behavior for real-time transcription and test grading.
     *
     * This function performs the following operations:
     * - Stops any currently playing audio.
     * - Initializes a recording session and captures audio through the microphone.
     * - Streams audio data via WebSocket to a transcription service.
     * - Processes real-time transcription events and updates final transcription data.
     * - Saves the recorded audio as a file and uploads it for grading and analysis.
     * - Updates UI and internal states to reflect results and system status.
     *
     * Key behaviors include:
     * - Managing microphone input using `navigator.mediaDevices.getUserMedia`.
     * - Handling WebSocket connection for streaming audio data and receiving transcription updates.
     * - Responding to transcription service events (e.g., partial/final transcriptions, errors).
     * - Periodically checking the status of the WebSocket connection for clean closure conditions.
     * - Uploading the recorded audio and transcription results for backend processing.
     *
     * Dependencies:
     * - `navigator.mediaDevices.getUserMedia` for microphone access.
     * - `WebSocket` for real-time audio data streaming.
     * - `MediaRecorder` and `Blob` for audio file creation.
     * - A variety of helper functions for transcription service integration, audio buffering, and UI/state updates.
     *
     * States managed:
     * - Final transcription text.
     * - Timer for audio recording duration.
     * - Audio blob data for file creation and upload.
     * - Response data such as grade score, matched/missed tests.
     */
    const handleTestPlayRecordStart = async () => {
        let time = timer
        let transcription = "",
            partial, // eslint-disable-line no-unused-vars
            isFinal;

        await stopCurrentPlayingAudio();

        setFinalTranscription('');
        setRecordingTest(true);
        setTestResults(false);

        let voiceStream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: true,
        });

        let micStream = new MicrophoneStream(); // ws
        stream.current = new MediaStream(voiceStream);

        let blobs = [];
        micStream.setStream(voiceStream); // ws

        let mediaRec = new MediaRecorder(stream.current, { mimeType: "audio/webm" });
        mediaRec.ondataavailable = (e) => blobs.push(e.data);

        let interval = setInterval(
            () => {
                time += 1
            },
            1000
        );

        if (isUrlExpired(presignedUrl)) {
            try {
                let res = await getAwsPresignedUrl();
                setPresignedUrl(res.data.url);
            } catch(err) {
                console.log(err)
            }
        }

        let ws = new WebSocket(presignedUrl); // ws
        ws.binaryType = "arraybuffer"; // ws

        /**
         * Event handler for the WebSocket `onopen` event.
         *
         * This function is triggered when a WebSocket connection
         * is successfully established. It allows you to implement
         * any functionality that needs to run after the connection
         * to the server is open, such as sending an initial message
         * or logging the connection status.
         */
        ws.onopen = function () {
            micStream.on("data", (e) => {
                const buffer = convertAudioToBinaryMessage(e);
                if (ws.readyState === 1) {
                    ws.send(buffer);
                    resetEventStreamMessage();
                }
            });
        };

        /**
         * Event handler for the 'message' event on a WebSocket.
         *
         * This function is triggered whenever a message is received from the server
         * over the WebSocket connection. The event contains the data sent by the server.
         *
         * @callback ws.onmessage
         * @param {MessageEvent} message - The event object representing the received message.
         * @property {string|Blob|ArrayBuffer} event.data - The data sent by the server, which
         * can be in string, Blob, or ArrayBuffer format depending on the WebSocket configuration.
         */
        ws.onmessage = function (message) {
            let messageWrapper = eventStreamMarshaller.decode(
                Buffer(message.data)
            );
            let messageBody = JSON.parse(
                String.fromCharCode.apply(String, messageWrapper.body)
            );

            switch (messageWrapper.headers[":message-type"].value) {
                case 'event':
                    //console.log('Message Body = %o', messageBody);
                    [transcription, partial, isFinal] = handleEventStreamMessage(messageBody, transcription);
                    break;

                case 'exception':
                    console.error('WS Error = %o', messageBody);
                    break;

                default:
                    console.log('MessageWrapper: %o', messageWrapper);
            }
        };

        /**
         * Event handler triggered when the MediaRecorder stops recording.
         *
         * This function is executed after the recording session ends, signaling
         * that the recorder has stopped capturing media data. It can be used to
         * process the recorded data, clean up resources, or handle any events
         * that should occur when the recording finishes.
         *
         * @event
         */
        mediaRec.onstop = async () => {
            micStream.stop(); // ws

            // Send an empty frame so that Transcribe initiates a closure of the WebSocket after submitting all transcripts
            let emptyMessage = getAudioEventMessage(Buffer.from(new Buffer([])));
            let emptyBuffer = eventStreamMarshaller.encode(emptyMessage);//marshall(emptyMessage);
            ws.send(emptyBuffer);

            // blobs.push(MediaRecorder.requestData());
            let blob = new Blob(blobs, { type: "audio/mp3" });
            //const url = window.URL.createObjectURL(blob);
            // create new simScripts with this audio url added
            let result = {};
            /**
             * Event handler for the 'onclose' event of a WebSocket.
             * This event is triggered when the WebSocket connection is closed.
             *
             * @callback WebSocket~onclose
             * @property {boolean} event.wasClean - Indicates whether the connection was cleanly closed.
             * @property {number} event.code - The WebSocket connection closure code.
             * @property {string} event.reason - The reason the connection was closed.
             */
            ws.onclose = async function () {
                clearInterval(interval);
                setTimer(time);
                if (time > 10) {
                    setPresignedBool(!presignedBool)
                }
                if (window.IS_DEVELOPMENT) {
                    console.log(transcription);
                }
                setFinalTranscription(transcription);
                //console.log(data)
                // Call Lambda grading function here.
                // Change below to save grade and transcription to the db instead of
                // calculating it in the back end
                const formData = new FormData();
                formData.append("sim_level_user_response", 1);
                formData.append("script_id", data._id);
                formData.append("audio_blob", blob);
                formData.append("transcribed_text", transcription);
                result = await uploadAudio(formData);
                if (window.IS_DEVELOPMENT) {
                    console.log(result.data);
                }
                setIsLoadingTestResults(false);
                setGradeScore(result.data.grade_score);
                setmatchTests(result.data.tests_matched);
                setmissesTests(result.data.missed_tests);

                await updateCurrentAudio();
            };
        };

        /*
          Once we've got the last message form the transcript service, close the WebSocket
         */
        let wsTimer = setInterval(() => {
            if (isFinal) {
                clearInterval(wsTimer);
                ws.close();
            }
        }, 500);

        rec.current = mediaRec;
        rec.current.start();
    };

    /**
     * A function that handles the process of stopping a recording test.
     *
     * This function is responsible for stopping the current recording,
     * clearing the associated media stream, updating the application's
     * state to stop the test, and preparing for recording test results.
     * It also resets the grade score before initiating result evaluation.
     */
    const handleRecordStopTest = () => {
        rec.current.stop();
        stream.current = null;
        setRecordingTest(false);
        setTestResults(true);
        setIsLoadingTestResults(true);
        setGradeScore(0);
    };

    /**
     * Handles the initiation of a recording session. This function stops any currently playing audio, initializes
     * the audio recording process, and sets up the necessary configurations for recording audio via the media devices API.
     * Once the recording is completed and stopped, the recorded audio file is processed, uploaded, and additional
     * application logic is executed based on the selected character type.
     *
     * The function performs the following actions:
     * - Stops any currently playing audio.
     * - Closes any open dialog boxes.
     * - Activates recording state for the application.
     * - Requests audio input permissions and initializes an audio stream.
     * - Sets up a MediaRecorder instance to capture and store audio data.
     * - Processes the recorded data upon stopping the MediaRecorder instance:
     *   - Converts captured data into an audio blob.
     *   - Uploads audio blob with associated metadata (e.g., script ID).
     *   - Handles character-specific audio generation logic.
     *   - Updates related application data (e.g., script index, audio metadata).
     *   - Refreshes the current audio state.
     * - Deactivates the loading state once the processing is complete.
     *
     * Note: This function works asynchronously, performing several operations sequentially, including asset uploads and state updates.
     */
    const handleRecordStart = async () => {
        await stopCurrentPlayingAudio();

        setOpenDialogBox(false);
        setIsRecording(true);

        voiceStream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: true,
        });
        stream.current = new MediaStream(voiceStream);
        blobs = [];
        rec.current = new MediaRecorder(stream.current, {
            mimeType: 'audio/webm',
        });
        rec.current.ondataavailable = (e) => blobs.push(e.data);
        /**
         * Event handler for the `onstop` event of the `rec.current` object.
         * This event is triggered when the recording process stops.
         *
         * You can assign a function to handle any cleanup or post-processing
         * tasks that need to occur once the recording has been stopped.
         *
         * Example uses may include saving recorded data, updating UI elements,
         * or releasing resources used during the recording session.
         */
        rec.current.onstop = async () => {
            setIsLoading(true);
            blob = new Blob(blobs, { type: 'audio/mp3' });
            const formData = new FormData();
            formData.append('script_id', data._id);
            formData.append('audio_blob', blob);
            const result = await uploadRecordedAudio(formData);
            if (selectedCharacter.type === 'agent') {
                await handleAudioGenerate(index,
                    result.data.url,
                    result.data.expiry_date,
                    result.data.audio_key, true);
            } else {
                await handleAudioGenerate(index,
                    result.data.url,
                    result.data.expiry_date,
                    result.data.audio_key,
                    false);
            }
            await updateData(index, data);
            await updateCurrentAudio();
            setIsLoading(false);
        };
        rec.current.start();
    };

    /**
     * Handles the stop action for the recording process.
     *
     * This function stops the active recording session by calling the `stop` method on the recorder instance.
     * It also clears the associated media stream and updates the recording state to ensure a consistent user experience.
     *
     * Side Effects:
     * - Stops the current recording session.
     * - Resets the media stream reference to null.
     * - Updates the recording status to `false`.
     */
    const handleRecordStop = () => {
        rec.current.stop();
        stream.current = null;
        setIsRecording(false);
    };

    /**
     * Displays a dialog box or initiates audio recording based on the presence of an audio URL.
     *
     * If `data.audio_url` is defined, the function sets the dialog box to an open state.
     * Otherwise, it triggers the start of the audio recording process.
     *
     * The function operates by checking the existence of an `audio_url` and either
     * setting a state variable to open the dialog or calling a recording handler.
     */
    const showDialogBox = () => {
        if (data.audio_url) {
            setOpenDialogBox(true);
        } else {
            handleRecordStart();
        }
    };

    /**
     * Determines and renders the appropriate microphone icon based on the recording state.
     *
     * @function showMicrophone
     * @param {boolean} recording - Indicates whether recording is currently active.
     * @returns {JSX.Element} A microphone icon component. If recording is active, it returns a `MicOffIcon`
     * with a stop recording handler. Otherwise, it returns a `MicIcon` with a dialog box handler.
     */
    const showMicrophone = (recording) => {
        if (recording) {
            return (
                <MicOffIcon className={classes.micOffIcon} onClick={handleRecordStop} />
            );
        }
        return <MicIcon className={classes.micIcon} onClick={showDialogBox} />;
    };

    /**
     * Highlights keywords in a given string by wrapping them in a styled span element.
     *
     * This function searches for all occurrences of the keywords in the provided array within
     * the input string and replaces them with span elements containing a background highlight.
     * The highlighting is case-insensitive.
     *
     * @param {string} str - The input string in which the keywords are to be highlighted.
     * @param {string[]} [arr=[]] - An array of keywords to be highlighted in the input string.
     * @returns {string} - The modified string with highlighted keywords.
     */
    const highLightKeywords = (str, arr = []) => {
        const keywordArray = [];
        arr.map((ele) => {
            const substring = new RegExp(ele, 'gi');
            const res = str.match(substring);
            if (res && res.length) {
                keywordArray.push(res[0]);
            }
            return keywordArray;
        });
        keywordArray.map((ele) => {
            str = str.replaceAll(
                ele,
                `<span title='Keyword' style='background: #ADBBD4;padding: 4px;border-radius: 3px;'>${ele}</span>`,
            );
            return str;
        });
        return str;
    };

    /**
     * Determines the border color based on the provided character type.
     *
     * @param {string} charachter - The character type used to determine the border color.
     *                               Expected values: 'coach', 'agent', 'customer', 'other'.
     *
     * @returns {string} The corresponding border color in hexadecimal format.
     */
    const getBorder = (charachter) => {
        let background;
        switch (charachter) {
            case 'coach':
                background = '#882647';
                break;
            case 'agent':
                background = '#EB6D20';
                break;
            case 'customer':
                background = '#2694D3';
                break;
            case 'other':
                background = '#2CB174';
                break;
            default:
                background = '#882647';
        }
        return background;
    };

    drag(ref);

    return (
        <>
            <Grid
                ref={ref}
                className={classes.gridStyle}
                style={{
                    borderLeft: `3px solid ${getBorder(selectedCharacter.type)}`,
                    background: data.newScript && bgColor ? '#b5e8d175' : '#ffff',
                    opacity,
                }}
                container
            >
                <Grid item className={classes.index}>
                    <Box>{index + 1}</Box>
                </Grid>
                <Grid item className={classes.scriptGrid}>
                    <Box className={classes.topBox}>
                        {selectedCharacter.type === 'agent'
                        && !switchGenerate
                        && !data.agent_recorded_audio
                            ? null
                            :showPlayButton(playAudio, data.audio_url, data)}

                        {selectedCharacter.type === 'agent'
                        && !switchGenerate
                        && !data.agent_recorded_audio
                            ? null
                            : showMicrophone(isRecording)}
                        <Dialog
                            open={openDialogBox}
                            onClose={() => setOpenDialogBox(false)}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">Record Audio</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    There is already a clip here. Are you sure you want to record
                                    over it?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDialogBox(false)} className={classes.cancelButton}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => handleRecordStart()}
                                    className={classes.dialogDeleteBtn}
                                    autoFocus
                                >
                                    OK
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Divider orientation="vertical" flexItem />
                        <CharacterAvatar
                            avatar={selectedCharacter.avatar}
                            borderColor={borderColor}
                            classes={classes.avatarImg}
                            alt={selectedCharacter.type}
                        />
                        <Typography component="div" className={classes.nameLabel}>
                            {selectedCharacter.name}
                            ,&nbsp;
                            <em className={classes.roleLabel}>
                                The&nbsp;
                                {selectedCharacter.masked_type
                                    ? selectedCharacter.masked_type : selectedCharacter.type}
                            </em>
                            <br />
                            {addTextArea ? (
                                <>
                                    <div className={classes.txtAreaContainer}>
                                        <TextareaAutosize
                                            required
                                            minRows={5}
                                            name="script"
                                            data-enable-grammarly="false"
                                            value={script}
                                            className={classes.textarea}
                                            onChange={(e) => handleTextChange(index, e)}
                                        />
                                        <div className={classes.textFielDiv}>
                                            <Button
                                                name="script"
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                className={classes.saveBtn}
                                                onClick={() => saveScript()}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="default"
                                                size="small"
                                                className={classes.cancelBtn}
                                                onClick={() => cancelScript()}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                    <Dialog
                                        open={openScriptDialogBox}
                                        onClose={() => setOpenScriptDialogBox(false)}
                                        aria-labelledby="alert-dialog-title"
                                        aria-describedby="alert-dialog-description"
                                    >
                                        <DialogContent>
                                            <DialogContentText id="alert-dialog-description">
                                                Do you want to auto generate audio?
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button
                                                onClick={() => {
                                                    setOpenScriptDialogBox(false);
                                                    showTextArea(false);
                                                }}
                                                className={classes.cancelButton}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    showPlayAudio();
                                                    setOpenScriptDialogBox(false);
                                                    showTextArea(false);
                                                }}
                                                className={classes.dialogDeleteBtn}
                                                autoFocus
                                            >
                                                OK
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Dialog
                                        open={openExistingScriptDialogBox}
                                        onClose={() => setOpenExistingScriptDialogBox(false)}
                                        aria-labelledby="alert-dialog-title"
                                        aria-describedby="alert-dialog-description"
                                    >
                                        <DialogContent>
                                            <DialogContentText id="alert-dialog-description">
                                                There is already a clip here. Are you sure you want
                                                {' '}
                                                <br />
                                                {' '}
                                                to record over it?
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button
                                                onClick={() => {
                                                    setOpenExistingScriptDialogBox(false);
                                                    showTextArea(false);
                                                }}
                                                color="primary"
                                            >
                                                No
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    showPlayAudio();
                                                    setOpenExistingScriptDialogBox(false);
                                                    showTextArea(false);
                                                }}
                                                className={classes.dialogDeleteBtn}
                                                autoFocus
                                            >
                                                Yes
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </>
                            ) : (
                                <Typography className={classes.descriptionLabel}>
                                    {data.script === '' ? (
                                        'Empty'
                                    ) : (
                                        <span>
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: highLightKeywords(data.script, data.keywords),
                                                }}
                                            />
                                        </span>
                                    )}
                                    ..
                                    <EditIcon
                                        className={classes.editIcon}
                                        onClick={() => showTextArea(true)}
                                    />
                                </Typography>
                            )}
                        </Typography>
                        <Tooltip title="Delete Script">
                            <DeleteOutlineIcon
                                className={classes.deleteIcon}
                                onClick={() => handleDeleteScript(index, data)}
                            />
                        </Tooltip>
                    </Box>
                    <br />
                    <Box className={classes.bottomBox}>
                        {isLoading ? <LinearProgress color="secondary" /> : null}
                        <Divider />
                        <Toolbar className={classes.toolBar}>
                            {selectedCharacter.type === 'agent' ? (
                                <div style={{ display: 'flex' }}>
                                    {sim.natural_lenguage !== true ? (
                                        <Tooltip title={keywords.length === 0 ? 'None' : keywords.slice(0, 20).join(' , ')} arrow>
                                            <Typography
                                                className={classes.toolBarLabel}
                                                onClick={handleKeywordOpen}
                                            >
                                                Keywords :
                                                {data.keywords.length ? (
                                                    <em className={classes.toolBarValue}>
                                                        {data.keywords.length <= 1
                                                            ? data.keywords.join(',')
                                                            : `${data.keywords.slice(0, 1).join(',')} ...`}
                                                    </em>
                                                ) : (
                                                    <em className={classes.toolBarValue}>None</em>
                                                )}
                                            </Typography>
                                        </Tooltip>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center'}}>
                                            <Tooltip title={'Manage Tests of This Script'} arrow>
                                                <Typography
                                                    className={classes.toolBarLabel}
                                                    onClick={handleTestOpen}
                                                >
                                                    Manage Tests
                                                </Typography>
                                            </Tooltip>
                                            <Divider
                                                className={classes.verticalLine}
                                                orientation="vertical"
                                                flexItem
                                            />
                                            <Typography component={'span'} className={classes.toolBarLabel}>
                                                Confidence %
                                                <FormControlLabel
                                                    control={(
                                                        <TextField
                                                            style={{width: '50px', marginLeft: '20px', marginRight: '10px'}}
                                                            onChange={handleScriptOveralConfidenceChange}
                                                            name="scriptOveralConfidence"
                                                            value={scriptOveralConfidence}
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Typography>
                                            <Divider
                                                className={classes.verticalLine}
                                                orientation="vertical"
                                                flexItem
                                            />
                                            <Tooltip title={'record your self to try the test'} arrow>
                                                <Typography
                                                    className={classes.toolBarLabel}
                                                    onClick={handleTryTestOpen}
                                                >
                                                    Try Out
                                                </Typography>
                                            </Tooltip>
                                        </div>
                                    )}
                                    <Dialog
                                        open={tryTestOpen}
                                        onClose={handleTryTestClose}
                                        aria-labelledby="test"
                                    >
                                        <DialogTitle id="test">Try Test</DialogTitle>
                                        <DialogContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'start', flexDirection: 'column', minWidth: '500px', justifyContent: 'center' }}>
                                                <Typography>
                                                    You can try the tests
                                                </Typography>
                                                <Typography>
                                                    Actual Script: {script}
                                                </Typography>
                                                <Typography>
                                                    Confidence: {scriptOveralConfidence}%
                                                </Typography>
                                            </div>
                                            {recordingTest ? (
                                                <>
                                                    <IconButton
                                                        color="primary"
                                                        aria-label="stop mic recording"
                                                        component="span"
                                                        size="medium"
                                                        className={classes.recButton}
                                                        onClick={handleRecordStopTest}
                                                    >
                                                        <MicOffIcon />
                                                    </IconButton>
                                                    {/* <div className={classes.centerElement} style={{ display: record ? 'flex' : 'none' }}>
                          <ReactMic
                            record={record}
                            className="sound-wave"
                            width={150}
                            strokeColor="#f27e7f"
                            backgroundColor="#fcfcfc"
                          />
                        </div> */}
                                                </>
                                            ) : (
                                                <IconButton
                                                    color="primary"
                                                    aria-label="start mic recording"
                                                    component="span"
                                                    size="medium"
                                                    className={classes.pauseButton}
                                                    onClick={handleTestPlayRecordStart}
                                                    disableFocusRipple={false}
                                                >
                                                    <MicIcon />
                                                </IconButton>
                                            ) }
                                            {testResults ? (
                                                (!isLoadingTestResults ) ?
                                                    (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                                            {(gradeScore >= scriptOveralConfidence ? <CheckIcon className={classes.checkIcon} style={{color: 'green', fontSize: '4rem'}}/> : <ClearIcon className={classes.crossIcon} color="error" style={{fontSize: '4rem'}}/>)}
                                                            <Typography>
                                                                transcription: {finalTranscription}
                                                            </Typography>
                                                            <Typography>
                                                                score: {gradeScore.toFixed(2)}%
                                                            </Typography>
                                                            {(matchTests.length > 0 || missesTests.length > 0 ) ?
                                                                (<Grid item xs={12} className={classes.grid} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                                                    <InputLabel style={{color: 'black', marginBottom: '5%'}}>
                                                                        Tests
                                                                    </InputLabel>
                                                                    <ul style={{width: '70%', margin: '0', padding: '0'}}>
                                                                        {matchTests.map(
                                                                            (correct) => (
                                                                                <li className={classes.list} key={correct.name} style={{display: 'flex', justifyContent: 'space-between', borderTop: '1px solid black', paddingLeft: '5%', paddingRight: '5%'}}>
                                                                                    { correct.name }
                                                                                    <div style={{ display: 'flex', alignItems: 'center'}}>
                                                                                        <CheckIcon className={classes.checkIcon} style={{color: 'green'}} />
                                                                                        {correct.grade.toFixed(2)}%
                                                                                    </div>
                                                                                </li>
                                                                            ),
                                                                        )}
                                                                        {missesTests.map(
                                                                            (missed) => (
                                                                                <li className={classes.list} key={missed.name} style={{display: 'flex', justifyContent: 'space-between' ,borderTop: '1px solid black', paddingLeft: '5%', paddingRight: '5%'}}>
                                                                                    { missed.name }
                                                                                    <div style={{ display: 'flex', alignItems: 'center'}}>
                                                                                        <ClearIcon className={classes.crossIcon} color="error" />
                                                                                        {missed.grade.toFixed(2)}%
                                                                                    </div>
                                                                                </li>
                                                                            ),
                                                                        )}
                                                                    </ul>
                                                                </Grid>) :
                                                                (<h2>No test are present</h2>)}
                                                        </div>
                                                    ) :
                                                    <div className={classes.loader}>
                                                        <CircularProgress color="primary"/>
                                                    </div>
                                            ) : '' }
                                        </DialogContent>
                                        <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Button onClick={handleTryTestClose} className={classes.cancelButton}>
                                                Cancel
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Dialog
                                        open={openTest}
                                        onClose={handleTestClose}
                                        aria-labelledby="test"
                                    >
                                        <DialogTitle id="test">
                                            Manage Tests
                                            <Button
                                                onClick={handleTryTestOpen}
                                                style= {{ position: 'relative', left: '60%', bottom: '5px' }}
                                            >
                                                Try Out
                                            </Button>
                                        </DialogTitle>
                                        <DialogContent>
                                            <div style={{ display: 'flex', alignItems: 'center', minWidth: '500px' }}>
                                                {tests.map((val, index) => (
                                                    <div key={index} style={{ marginRight: '10px' }}>
                                                        <Button onClick={() => handleTestEdit(index)} className={classes.cancelButton}>{val.name}</Button>
                                                    </div>
                                                ))}
                                                <Button onClick={() => handleTestEdit(-1)} className={classes.cancelButton}>+</Button>
                                            </div>
                                            {openTestEdit ? (
                                                <div>
                                                    <TextField
                                                        fullWidth
                                                        label="Name"
                                                        variant="outlined"
                                                        value={name}
                                                        onChange={handleNameChange}
                                                        required
                                                        helperText={'Enter a name for the Test'}
                                                        error={testNameError.length > 0}
                                                    />
                                                    <br />
                                                    <br />
                                                    <FormControl
                                                        className={classes.radioBtnDiv}
                                                        component="fieldset"
                                                    >
                                                        <FormLabel
                                                            className={classes.radioLabels}
                                                            component="legend"
                                                        >
                                                            <Typography className={classes.toolBarLabel}>
                                                                Phrasing:
                                                            </Typography>
                                                        </FormLabel>
                                                        <RadioGroup
                                                            defaultValue="Flexible"
                                                            aria-label="Flexible"
                                                            className={classes.radioBtnDiv}
                                                        >
                                                            <FormControlLabel
                                                                className={classes.radioBtn}
                                                                value="flexible"
                                                                control={(
                                                                    <Radio
                                                                        checked={isFlexible}
                                                                        value="flexible"
                                                                        onChange={(e) => handleIsFlexibleChange(e)}
                                                                        className={classes.radio}
                                                                    />
                                                                )}
                                                                label={<span className={classes.labelFont}>Flexible</span>}
                                                            />
                                                            <FormControlLabel
                                                                className={classes.radioBtn}
                                                                value="strict"
                                                                control={(
                                                                    <Radio
                                                                        checked={!isFlexible}
                                                                        value="strict"
                                                                        onChange={(e) => handleIsFlexibleChange(e)}
                                                                        className={classes.radio}
                                                                    />
                                                                )}
                                                                label={<span className={classes.labelFont}>Strict</span>}
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                    <br />
                                                    <br />
                                                    <TextField
                                                        fullWidth
                                                        label="Coach Intention"
                                                        variant="outlined"
                                                        value={coachFeedback}
                                                        onChange={handleCoachFeedbackChange}
                                                    />
                                                    <br />
                                                    <br />

                                                    {!editMode && (
                                                        <div style={{display: 'flex'}}>
                                                            <TagsInput
                                                                value={testKeywords}
                                                                onlyUnique
                                                                className={classes.tagInput}
                                                                name="testKeywords"
                                                                onChange={handleKeywordsChange}
                                                                inputProps={{
                                                                    disabled: true,
                                                                    placeholder: ' ',
                                                                    style: { width: '100px' }
                                                                }}
                                                            />
                                                            <Button onClick={handleEditClick} color="primary">Edit</Button>
                                                        </div>
                                                    )}

                                                    {/* Display the regular input field in edit mode */}
                                                    {editMode && (
                                                        <div style={{display: 'flex'}}>
                                                            <TextField
                                                                type="text"
                                                                value={editedTags}
                                                                label="Keywords"
                                                                variant="outlined"
                                                                onChange={(e) => setEditedTags(e.target.value)}
                                                                helperText={'Please enter at least one keyword'}
                                                                error={testWordsError.length > 0}
                                                            />
                                                            <Button onClick={handleSaveClick} color="primary">Save</Button>
                                                        </div>
                                                    )}
                                                    {!errorTag && (
                                                        <em className={classes.toolBarValue}>
                                                            Separate the tags by comma, use only one operator & or |
                                                        </em>
                                                    )}
                                                    {errorTag && (
                                                        <em className={classes.toolBarValue} style={{color:'red'}}>
                                                            Use only one operator & or |
                                                        </em>
                                                    )}
                                                    <br />
                                                    {testWordsError && (
                                                        <em className={classes.toolBarValue} style={{color:'red'}}>
                                                            Please enter at least one keyword
                                                        </em>
                                                    )}
                                                    <br />
                                                    <br />
                                                    {!hideConfidence && (
                                                        <TextField
                                                            fullWidth
                                                            label="Confidence %"
                                                            variant="outlined"
                                                            value={confidence}
                                                            onChange={handleConfidenceChange}
                                                        />
                                                    )}
                                                </div>
                                            ) : null }
                                        </DialogContent>
                                        <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            {openTestEdit ? (
                                                    <>
                                                        <Button onClick={handleRemoveTest} className={classes.cancelButton}>
                                                            Delete
                                                        </Button>
                                                        <div>
                                                            <Button onClick={handleTestCancel} className={classes.cancelButton}>
                                                                Cancel
                                                            </Button>
                                                            <Button onClick={handleTestSave} color="primary">
                                                                Save
                                                            </Button>
                                                        </div>
                                                    </>
                                                ) :
                                                <Button onClick={handleTestCloseModal} className={classes.cancelButton}>
                                                    Cancel
                                                </Button>
                                            }
                                        </DialogActions>
                                    </Dialog>
                                    <Dialog
                                        open={openKeyword}
                                        onClose={handleKeywordClose}
                                        aria-labelledby="keywords"
                                    >
                                        <DialogTitle id="keywords">Add Keyword</DialogTitle>
                                        <DialogContent>
                                            <TagsInput
                                                onlyUnique
                                                value={keywords}
                                                className={classes.tagInput}
                                                name="keywords"
                                                onChange={(e) => keywordsChange(e)}
                                                inputProps={{
                                                    placeholder: 'Add Keyword',
                                                    style: { width: '100px' },
                                                }}
                                            />
                                            <em className={classes.toolBarValue}>
                                                Click on &apos;Enter&apos; button after adding each keyword
                                                and then save.
                                            </em>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={handleKeywordCancel} className={classes.cancelButton}>
                                                Cancel
                                            </Button>
                                            <Button onClick={handleKeywordSave} color="primary">
                                                Save
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Divider
                                        className={classes.verticalLine}
                                        orientation="vertical"
                                        flexItem
                                    />
                                </div>
                            ) : null }
                            <Tooltip title={data.hint_text.length === 0 ? 'None' : data.hint_text} arrow>
                                <Typography
                                    className={classes.toolBarLabel}
                                    onClick={handleClickOpen}
                                >
                                    Hint Text:
                                    {data.hint_text.length ? (
                                        <em className={classes.toolBarValue}>
                                            {data.hint_text.length > 10
                                                ? `${data.hint_text.slice(0, 2)} ...`
                                                : data.hint_text}
                                        </em>
                                    ) : (
                                        <em className={classes.toolBarValue}>None</em>
                                    )}
                                </Typography>
                            </Tooltip>
                            <Dialog
                                open={open}
                                onClose={handleClose}
                                aria-labelledby="hint_text"
                                fullWidth
                                maxWidth="sm"
                            >
                                <DialogTitle id="hint_text">Add Hint Text</DialogTitle>
                                <DialogContent>
                                    <TextField
                                        autoFocus
                                        id="hint_texts"
                                        margin="dense"
                                        name="hint_text"
                                        value={hintText}
                                        multiline
                                        minRows={3}
                                        onChange={(e) => handleTextChange(index, e)}
                                        fullWidth
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={hintTextCancel} className={classes.cancelButton}>
                                        Cancel
                                    </Button>
                                    <Button onClick={hintTextSave} color="primary">
                                        Save
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Divider
                                className={classes.verticalLine}
                                orientation="vertical"
                                flexItem
                            />
                            <FormControl component="fieldset">
                                <FormGroup
                                    className={classes.formGroup}
                                    aria-label="position"
                                    row
                                >
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                value="hide_text"
                                                name="hide_text"
                                                checked={!!data.hide_text}
                                                onBlur={save}
                                                // size="small"
                                                color="primary"
                                                onChange={(e) => handleCheckboxChange(index, e)}
                                                className={classes.formControlLabel}
                                            />
                                        )}
                                        label={(
                                            <Typography
                                                className={classes.toolBarLabel}
                                            >
                                                Hide Text
                                            </Typography>
                                        )}
                                        labelPlacement="start"
                                    />
                                </FormGroup>
                            </FormControl>
                            <Divider
                                className={classes.verticalLine}
                                orientation="vertical"
                                flexItem
                            />
                            {(selectedCharacter.type === 'agent' && sim.real_time_feedback) && (
                                <>
                                    <div className={classes.radioBtnDiv}>
                                        <Toolbar className={classes.toolbar}>
                                            <FormControl
                                                className={classes.radioBtnDiv}
                                                component="fieldset"
                                            >
                                                <FormLabel
                                                    className={classes.radioLabels}
                                                    component="legend"
                                                >
                                                    <Typography className={classes.toolBarLabel}>
                                                        Real-time feedback
                                                    </Typography>
                                                </FormLabel>
                                                <RadioGroup
                                                    defaultValue="yes"
                                                    aria-label="feedback"
                                                    className={classes.radioBtnDiv}
                                                >
                                                    <FormControlLabel
                                                        className={classes.radioBtn}
                                                        value="yes"
                                                        control={(
                                                            <Radio
                                                                checked={data.real_time_feedback}
                                                                value="yes"
                                                                onBlur={save}
                                                                onChange={(e) => handleRadioChange(index, e)}
                                                                className={classes.radio}
                                                            />
                                                        )}
                                                        label={<span className={classes.labelFont}>Yes</span>}
                                                    />
                                                    <FormControlLabel
                                                        className={classes.radioBtn}
                                                        value="no"
                                                        control={(
                                                            <Radio
                                                                checked={!data.real_time_feedback}
                                                                value="no"
                                                                onBlur={save}
                                                                onChange={(e) => handleRadioChange(index, e)}
                                                                className={classes.radio}
                                                            />
                                                        )}
                                                        label={<span className={classes.labelFont}>No</span>}
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Toolbar>
                                    </div>
                                </>
                            )}
                            {selectedCharacter.type === 'agent' && sim.real_time_feedback ? (
                                <Divider
                                    className={classes.verticalLine}
                                    orientation="vertical"
                                    flexItem
                                />
                            ) : null}
                            <GenerateAudio
                                data={data}
                                selectedCharacter={selectedCharacter}
                                setSwitchGenerate={setSwitchGenerate}
                                switchGenerate={switchGenerate}
                                handleAgentRecordedAudio={handleAgentRecordedAudio}
                                index={index}
                                updateData={updateData}
                                handleAudioChange={handleAudioChange}
                                genAudio={genAudio}
                                setVoice={setVoice}
                                currentVoice={currentVoice}
                                setGenAudio={setGenAudio}
                                node={node}
                                voice={voice}
                                newSim={newSim}
                                showPlayAudio={showPlayAudio}
                            />
                        </Toolbar>
                    </Box>
                    {sim.auto_populate ? (
                        <Autopopulate
                            data={data}
                            tabs={tabs}
                            handleResetAutopopulate={handleResetAutopopulate}
                            index={index}
                            updateData={updateData}
                            handleAutopopulateChange={handleAutopopulateChange}
                            caseInformationArray={caseInformationArray}
                            patientInformationArray={patientInformationArray}
                            acmeInformationArray={acmeInformationArray}
                            loremIpsumInformationArray={loremIpsumInformationArray}
                        />
                    ) : null}
                </Grid>
            </Grid>
        </>
    );
};

/**
 * PropTypes definition for the CharacterData component.
 * This defines the expected types for the props that the
 * CharacterData component should receive.
 */
CharacterData.propTypes = {
    index: PropTypes.number,
    data: PropTypes.object,
    characterData: PropTypes.array,
    handleChange: PropTypes.func,
    handleAudioChange: PropTypes.func,
    handleKeywordChange: PropTypes.func,
    handleTestChange: PropTypes.func,
    handleCheckboxChange: PropTypes.func,
    handleAudioGenerate: PropTypes.func,
    handleRadioChange: PropTypes.func,
    handleDeleteScript: PropTypes.func,
    handleAgentRecordedAudio: PropTypes.func,
    handleAutopopulateChange: PropTypes.func,
    handleResetAutopopulate: PropTypes.func,
    updateData: PropTypes.func,
    borderColor: PropTypes.string,
    sim: PropTypes.object,
    tabs: PropTypes.array,
    currentAudio: PropTypes.object,
    setTimePlayfun: PropTypes.func,
    timePlay: PropTypes.number,
    pauseFunction: PropTypes.func,
    levelOrder: PropTypes.number,
    newSim: PropTypes.object,
    currentVoice: PropTypes.any,
    path: PropTypes.any,
    onlyKeywords: PropTypes.bool,
};

export default CharacterData;
